import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { listingsApi, categoriesApi, citiesApi, uploadApi, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, ArrowLeft, MapPin, ImagePlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_IMAGES = 5;

function resolveImageUrl(img) {
  if (!img) return null;
  if (img.startsWith('data:')) return img;
  if (img.startsWith('/api/')) return `${API_URL}${img}`;
  if (img.startsWith('http')) return img;
  return img;
}

export default function EditListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // images: [{url: string, preview: string, isNew: bool, file?: File}]
  const [images, setImages] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    price: '',
    city: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, listingRes, citiesRes] = await Promise.all([
          categoriesApi.getAll(),
          listingsApi.getOne(id),
          citiesApi.getAll()
        ]);
        setCategories(categoriesRes.data);
        setCities(citiesRes.data);
        
        const listing = listingRes.data;
        setFormData({
          title: listing.title || '',
          category: listing.category || '',
          description: listing.description || '',
          price: listing.price || '',
          city: listing.city || '',
        });

        // Load existing images
        const existingImages = (listing.images || []).map(url => ({
          url,
          preview: resolveImageUrl(url),
          isNew: false,
        }));
        setImages(existingImages);
      } catch (error) {
        toast.error('Failed to load listing');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.isNew && img.preview?.startsWith('blob:')) URL.revokeObjectURL(img.preview);
      });
    };
  }, [images]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleCityChange = (value) => {
    setFormData(prev => ({ ...prev, city: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - images.length;

    if (files.length > remaining) {
      toast.error(`You can add ${remaining} more image${remaining !== 1 ? 's' : ''} (max ${MAX_IMAGES})`);
    }

    const validFiles = files.slice(0, remaining).filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    const newImages = validFiles.map(file => ({
      url: null,
      preview: URL.createObjectURL(file),
      isNew: true,
      file,
    }));

    setImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => {
      const removed = prev[index];
      if (removed.isNew && removed.preview?.startsWith('blob:')) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (images.length === 0) {
      toast.error('Please add at least 1 image');
      return;
    }

    setSaving(true);
    setUploading(true);

    try {
      // Upload new images, keep existing URLs
      const finalUrls = [];
      for (const img of images) {
        if (img.isNew && img.file) {
          try {
            const res = await uploadApi.uploadImage(img.file);
            finalUrls.push(res.data.url);
          } catch (err) {
            toast.error('Failed to upload an image. Skipping.');
          }
        } else if (img.url) {
          finalUrls.push(img.url);
        }
      }

      if (finalUrls.length === 0) {
        toast.error('All image uploads failed. Please try again.');
        setSaving(false);
        setUploading(false);
        return;
      }

      setUploading(false);

      const payload = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : null,
        images: finalUrls,
        city: formData.city || null,
      };

      await listingsApi.update(id, payload);
      toast.success('Listing updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="edit-listing-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 
          className="text-3xl font-bold mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Edit Listing
        </h1>
        <p className="text-muted-foreground mb-8">
          Update your listing details
        </p>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="What are you listing?"
                  data-testid="title-input"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city">City (Optional)</Label>
                <Select
                  value={formData.city}
                  onValueChange={handleCityChange}
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="Select a city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name}, {city.province}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your listing in detail..."
                  rows={5}
                  data-testid="description-input"
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price (CAD) - Optional</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  data-testid="price-input"
                />
              </div>

              {/* Image Upload - Multi */}
              <div className="space-y-2">
                <Label>Images * <span className="text-muted-foreground font-normal">({images.length}/{MAX_IMAGES})</span></Label>

                {images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3" data-testid="image-previews">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-border group">
                        <img
                          src={img.preview}
                          alt={`Preview ${idx + 1}`}
                          className="w-full h-full object-cover"
                          data-testid={`image-preview-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 p-1 bg-background/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                            Cover
                          </span>
                        )}
                      </div>
                    ))}

                    {images.length < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-1 transition-colors"
                        data-testid="add-more-images-btn"
                      >
                        <ImagePlus className="h-5 w-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">Add</span>
                      </button>
                    )}
                  </div>
                )}

                {images.length === 0 && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="image-upload"
                    data-testid="image-upload-area"
                  >
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload images
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      1-{MAX_IMAGES} images required. Max 5MB each, JPG/PNG/WebP
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                  multiple
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={saving}
                  data-testid="update-listing-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {uploading ? 'Uploading images...' : 'Updating...'}
                    </>
                  ) : (
                    'Update Listing'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
